/** @format */

import React, { useEffect, useMemo, useState } from 'react'
import { _$, Radio, Dialog, Message, Checkbox, Tooltip } from '@king-design/react'
import styled from 'styled-components'
import { IconButton } from 'src/components'
import { TraceAnalysisListItem } from '../typescript'
import { handleExportForm } from 'src/utils/common'
import { TABLE_HEADERS, TableTabType, DEFAULT_HEADER_KEYS } from './TraceTable'

type ExportScope = 'allPage' | 'selected'

interface ExportTraceProps {
  className?: string
  checkedRow: string[]
  traceData: TraceAnalysisListItem[]
  tab: TableTabType
}

const ExportTrace: React.FC<ExportTraceProps> = ({ className, checkedRow, traceData, tab }) => {
  const [visible, setVisible] = useState(false)
  const [exportScope, setExportScope] = useState<ExportScope>('allPage')
  const [checkedHeader, setCheckedHeader] = useState<string[]>(() => [...DEFAULT_HEADER_KEYS])
  const isAllChecked = checkedHeader.length === DEFAULT_HEADER_KEYS.length
  const isIndeterminate = checkedHeader.length > 0 && !isAllChecked

  const visibleHeaders = useMemo(
    () => TABLE_HEADERS.filter(item => checkedHeader.includes(item.key)),
    [checkedHeader]
  )

  useEffect(() => {
    if (!checkedRow.length) {
      setExportScope('allPage')
    }
  }, [checkedRow])

  // 全选
  const handleSelectAll = (checked: boolean) => {
    setCheckedHeader(checked ? [...DEFAULT_HEADER_KEYS] : ['TraceId'])
  }

  // 选择表头
  const handleToggleHeader = (key: string, checked: boolean) => {
    if (checked) {
      setCheckedHeader(prev => (prev.includes(key) ? prev : [...prev, key]))
      return
    }
    setCheckedHeader(prev => prev.filter(item => item !== key))
  }

  // 导出
  const exportData = () => {
    if (!visibleHeaders.length) {
      Message.error(_$('请至少选择一个导出字段'))
      return
    }

    const list =
      exportScope === 'selected'
        ? traceData.filter(row =>
            checkedRow.includes(`${row.ServiceName}-${row.TraceId}-${row.SpanId}`)
          )
        : traceData

    if (!list.length) {
      Message.error(_$('暂无数据'))
      return
    }

    const mapdata = list.map(row =>
      visibleHeaders.map(header => {
        const value = row[header.key as keyof TraceAnalysisListItem]
        return value ?? ''
      })
    )
    const fileName = tab === 'rootSpan' ? _$('Root Span列表') : _$('Span列表')
    handleExportForm(visibleHeaders, mapdata, fileName)
    Message.success(_$('下载成功'))
    setVisible(false)
  }

  return (
    <div className={className} id="export-trace-container">
      <IconButton
        type="download"
        hoverBackground
        title={_$('导出')}
        onClick={() => {
          setVisible(true)
        }}
      />
      <Dialog
        title={_$('选择导出信息')}
        value={visible}
        width={600}
        okText={_$('导出')}
        cancelText={_$('取消')}
        container={'#export-trace-container'}
        onOk={exportData}
        onChangeValue={value => {
          setVisible(value)
        }}
      >
        <div className="export-trace-body">
          <div className="export-scope-row">
            <span className="export-scope-label">{_$('导出范围')}</span>
            <div className="export-scope-radios">
              <Radio
                name="exportScope"
                trueValue="allPage"
                value={exportScope}
                onChangeValue={value => setExportScope(value as ExportScope)}
              >
                {_$('导出当前页Span')}
              </Radio>
              {
                <Tooltip
                  theme="light"
                  disabled={!!checkedRow.length}
                  content={_$('请在列表中选择Span')}
                >
                  <Radio
                    disabled={!checkedRow.length}
                    name="exportScope"
                    trueValue="selected"
                    value={exportScope}
                    onChangeValue={value => setExportScope(value as ExportScope)}
                  >
                    {_$('仅导出选中Span')}
                  </Radio>
                </Tooltip>
              }
            </div>
          </div>
          <div className="export-header-section">
            <Checkbox
              className="export-select-all"
              value={isAllChecked}
              indeterminate={isIndeterminate}
              onChangeValue={value => handleSelectAll(value)}
            >
              {_$('全选')}
            </Checkbox>
            <div className="export-header-grid">
              {TABLE_HEADERS.map(item => {
                const checked = checkedHeader.includes(item.key)
                return (
                  <Checkbox
                    disabled={item.key === 'TraceId'}
                    key={item.key}
                    value={checked}
                    onChangeValue={value => handleToggleHeader(item.key, value)}
                  >
                    {item.value}
                  </Checkbox>
                )
              })}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default styled(ExportTrace)`
  .export-trace-body {
    padding-top: 8px;
  }

  .export-scope-row {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  }

  .export-scope-label {
    flex-shrink: 0;
    margin-right: 16px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.88);
  }

  .export-scope-radios {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .export-header-section {
    .export-select-all {
      margin-bottom: 16px;
    }
  }

  .export-header-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px 12px;
  }
`
